import { memo } from "react";

/**
 * Decorative animated water droplets + rising bubbles background.
 * - Pure CSS keyframes (no JS timers, no re-renders)
 * - GPU-friendly (transform + opacity only)
 * - pointer-events: none, aria-hidden
 * - Respects prefers-reduced-motion
 *
 * Usage:
 *   <WaterDropsBackground variant="page" />                      // absolute over the whole page
 *   <WaterDropsBackground variant="section" density="high" />    // absolute inside a section
 */

type Variant = "page" | "section";
type Density = "low" | "normal" | "high";

interface Props {
  variant?: Variant;
  density?: Density;
  className?: string;
}

// deterministic pseudo-random positions
const DROPS = [
  { l: 3, s: 10, d: 20, delay: 0, c: "b" },
  { l: 8, s: 7, d: 24, delay: 3.5, c: "t" },
  { l: 13, s: 12, d: 18, delay: 1.2, c: "b" },
  { l: 18, s: 8, d: 26, delay: 6.6, c: "t" },
  { l: 23, s: 11, d: 22, delay: 2.4, c: "b" },
  { l: 28, s: 6, d: 19, delay: 8.3, c: "t" },
  { l: 33, s: 14, d: 25, delay: 4.1, c: "b" },
  { l: 38, s: 9, d: 21, delay: 10.9, c: "t" },
  { l: 43, s: 12, d: 23, delay: 1.4, c: "b" },
  { l: 48, s: 7, d: 18, delay: 5.2, c: "t" },
  { l: 53, s: 10, d: 26, delay: 9.0, c: "b" },
  { l: 58, s: 13, d: 20, delay: 0.4, c: "t" },
  { l: 63, s: 8, d: 24, delay: 6.7, c: "b" },
  { l: 68, s: 11, d: 22, delay: 3.5, c: "t" },
  { l: 73, s: 9, d: 19, delay: 8.1, c: "b" },
  { l: 78, s: 12, d: 25, delay: 2.0, c: "t" },
  { l: 83, s: 7, d: 21, delay: 5.8, c: "b" },
  { l: 88, s: 13, d: 23, delay: 11.5, c: "t" },
  { l: 93, s: 10, d: 20, delay: 4.5, c: "b" },
  { l: 97, s: 8, d: 26, delay: 7.3, c: "t" },
  // desktop-only extras
  { l: 6, s: 9, d: 22, delay: 12.0, c: "b", md: true },
  { l: 20, s: 12, d: 24, delay: 4.8, c: "t", md: true },
  { l: 35, s: 7, d: 20, delay: 9.3, c: "b", md: true },
  { l: 50, s: 14, d: 26, delay: 2.5, c: "t", md: true },
  { l: 66, s: 8, d: 21, delay: 11.5, c: "b", md: true },
  { l: 82, s: 11, d: 23, delay: 6.1, c: "t", md: true },
];

const BUBBLES = [
  { l: 6, s: 18, d: 26, delay: 0, o: 0.22 },
  { l: 20, s: 26, d: 34, delay: 5, o: 0.16 },
  { l: 34, s: 14, d: 24, delay: 2.5, o: 0.26 },
  { l: 48, s: 30, d: 38, delay: 8, o: 0.14 },
  { l: 60, s: 20, d: 28, delay: 3.5, o: 0.22 },
  { l: 72, s: 16, d: 24, delay: 11, o: 0.24 },
  { l: 84, s: 24, d: 32, delay: 6, o: 0.16 },
  { l: 94, s: 18, d: 28, delay: 2, o: 0.20 },
  { l: 14, s: 22, d: 34, delay: 7, o: 0.14, md: true },
  { l: 42, s: 28, d: 36, delay: 4.5, o: 0.12, md: true },
  { l: 78, s: 20, d: 28, delay: 9.5, o: 0.18, md: true },
];

const AMBIENT = [
  { l: 10, t: 20, s: 140, d: 22, delay: 0 },
  { l: 70, t: 45, s: 180, d: 26, delay: 4 },
  { l: 35, t: 70, s: 160, d: 24, delay: 8 },
  { l: 85, t: 85, s: 120, d: 20, delay: 3 },
];

const Drop = ({ size, color }: { size: number; color: string }) => (
  <svg
    width={size}
    height={size * 1.35}
    viewBox="0 0 24 32"
    fill="none"
    style={{ display: "block", filter: "drop-shadow(0 2px 4px rgba(20,159,232,.35))" }}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id={`wd-g-${color}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color === "t" ? "#7CF0E1" : "#9BD8F3"} stopOpacity="0.95" />
        <stop offset="100%" stopColor={color === "t" ? "#0FB5A3" : "#087EC0"} stopOpacity="1" />
      </linearGradient>
    </defs>
    <path
      d="M12 1 C12 1 3 13 3 20 a9 9 0 0 0 18 0 C21 13 12 1 12 1 Z"
      fill={`url(#wd-g-${color})`}
    />
    <ellipse cx="9" cy="17" rx="2.4" ry="3.6" fill="#FFFFFF" opacity="0.65" />
    <circle cx="14" cy="22" r="1" fill="#FFFFFF" opacity="0.5" />
  </svg>
);

const WaterDropsBackground = ({ variant = "page", density = "normal", className = "" }: Props) => {
  const densityFactor = density === "high" ? 1.15 : density === "low" ? 0.7 : 1;

  return (
    <div
      aria-hidden="true"
      className={`wdrops absolute inset-0 pointer-events-none overflow-hidden ${className}`}
      style={{ zIndex: 0 }}
    >
      <style>{`
        .wdrops .wd-drop {
          position: absolute;
          top: -40px;
          will-change: transform, opacity;
          animation-name: wd-fall;
          animation-timing-function: cubic-bezier(.45,.05,.55,1);
          animation-iteration-count: infinite;
          opacity: 0;
        }
        .wdrops .wd-bubble {
          position: absolute;
          bottom: -60px;
          border-radius: 9999px;
          background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.75), rgba(95,230,213,.42) 45%, rgba(20,159,232,.18) 100%);
          box-shadow: inset 0 0 10px rgba(255,255,255,.5);
          will-change: transform, opacity;
          animation-name: wd-rise;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          opacity: 0;
        }
        .wdrops .wd-ambient {
          position: absolute;
          border-radius: 9999px;
          background: radial-gradient(circle at 35% 35%, rgba(95,230,213,.32), rgba(20,159,232,.14) 55%, transparent 75%);
          filter: blur(22px);
          will-change: transform;
          animation-name: wd-float;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          animation-direction: alternate;
        }
        @keyframes wd-fall {
          0%   { transform: translate3d(0,0,0) scale(1);   opacity: 0; }
          6%   { opacity: .95; }
          82%  { transform: translate3d(0, 90vh, 0) scale(1); opacity: .9; }
          92%  { transform: translate3d(0, 96vh, 0) scale(1.4, .5); opacity: .55; }
          100% { transform: translate3d(0, 100vh, 0) scale(1.7, .28); opacity: 0; }
        }
        @keyframes wd-rise {
          0%   { transform: translate3d(0,0,0);              opacity: 0; }
          15%  { opacity: 1; }
          50%  { transform: translate3d(14px, -55vh, 0);     opacity: 1; }
          85%  { opacity: .85; }
          100% { transform: translate3d(-10px, -110vh, 0);   opacity: 0; }
        }
        @keyframes wd-float {
          0%   { transform: translate3d(0,0,0) scale(1); }
          100% { transform: translate3d(24px,-34px,0) scale(1.18); }
        }
        .wdrops.is-section .wd-drop { animation-name: wd-fall-section; }
        .wdrops.is-section .wd-bubble { animation-name: wd-rise-section; }
        @keyframes wd-fall-section {
          0%   { transform: translate3d(0,0,0); opacity: 0; }
          8%   { opacity: .9; }
          88%  { transform: translate3d(0, 100%, 0); opacity: .85; }
          100% { transform: translate3d(0, 112%, 0); opacity: 0; }
        }
        @keyframes wd-rise-section {
          0%   { transform: translate3d(0,0,0); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translate3d(0,-115%,0); opacity: 0; }
        }
        @media (max-width: 640px) {
          .wdrops .wd-md { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .wdrops .wd-drop, .wdrops .wd-bubble, .wdrops .wd-ambient { animation: none !important; opacity: .18 !important; }
        }
      `}</style>

      <div className={`${variant === "section" ? "is-section " : ""}absolute inset-0`}>
        {AMBIENT.map((a, i) => (
          <div
            key={`a-${i}`}
            className="wd-ambient"
            style={{
              left: `${a.l}%`,
              top: `${a.t}%`,
              width: a.s,
              height: a.s,
              animationDuration: `${a.d}s`,
              animationDelay: `${a.delay}s`,
            }}
          />
        ))}

        {DROPS.map((d, i) => (
          <div
            key={`d-${i}`}
            className={`wd-drop ${d.md ? "wd-md" : ""}`}
            style={{
              left: `${d.l}%`,
              animationDuration: `${d.d / densityFactor}s`,
              animationDelay: `${d.delay}s`,
            }}
          >
            <Drop size={d.s} color={d.c} />
          </div>
        ))}

        {BUBBLES.map((b, i) => (
          <div
            key={`b-${i}`}
            className={`wd-bubble ${b.md ? "wd-md" : ""}`}
            style={{
              left: `${b.l}%`,
              width: b.s,
              height: b.s,
              opacity: b.o,
              animationDuration: `${b.d / densityFactor}s`,
              animationDelay: `${b.delay}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(WaterDropsBackground);
